/**
 * The preview column while the catalogue is still on its way.
 *
 * **The column stands from the FIRST frame**, and that is the whole reason this file exists. With
 * no second child in the pane the table is `flex: 1` against nothing and takes the whole measure, so
 * the panel arriving would narrow every column by 396px plus the gap — measured on the clients
 * roster at exactly that, which is where the rule comes from.
 *
 * It draws the panel's own SHAPE — the identity row, the still card, the muscle card with its two
 * figures and a pill row — in `shared/ui/skeleton`'s bars, gated: the gate hides the BARS, never the
 * box, so a catalogue answered inside the 150ms grace draws no bars at all and one that took longer
 * does not flicker.
 *
 * The equipment card is deliberately absent. It is the LAST card in the column and holds its
 * shorter shape, so a wait that drew one would leave a hole when a bodyweight movement landed.
 */

import { useTranslation } from 'react-i18next';

import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import styles from './ExercisePreviewPanel.module.css';

export function ExercisePreviewSkeleton({ skeleton }: { readonly skeleton: boolean }) {
  const { t } = useTranslation();

  return (
    <aside className={styles.panel} aria-busy="true" aria-label={t('web:exerciseLibrary.panel.name')}>
      <p className={hidden.srOnly}>{t('web:exerciseLibrary.states.loading')}</p>

      <div className={styles.identity}>
        <div>
          {skeleton && (
            <>
              <i className={`${String(skel.bar)} ${String(styles.skelName)}`} />
              <i className={`${String(skel.bar)} ${String(styles.skelSub)}`} />
            </>
          )}
        </div>
      </div>

      <section className={`${String(styles.card)} ${String(styles.stillCard)}`}>
        {skeleton && <i className={`${String(skel.bar)} ${String(styles.skelStill)}`} />}
      </section>

      <section className={`${String(styles.card)} ${String(styles.muscleCard)}`}>
        {skeleton && (
          <>
            <span className={styles.skelFigures}>
              <i className={`${String(skel.bar)} ${String(styles.skelFigure)}`} />
              <i className={`${String(skel.bar)} ${String(styles.skelFigure)}`} />
            </span>
            <span className={`${String(styles.pills)} ${String(styles.skelPills)}`}>
              <i className={`${String(skel.bar)} ${String(styles.skelPill)}`} />
              <i className={`${String(skel.bar)} ${String(styles.skelPill)}`} />
            </span>
          </>
        )}
      </section>
    </aside>
  );
}
